import type { ProductBehavior } from '@up2stay/up2promo-api-client';
import type {
  LocationRoutingWarning,
  ProductFormLocation,
} from '@up2stay/up2promo-commons/products';
import { Button, Label } from '@up2stay/up2promo-ui';
import { MapPin } from 'lucide-react';
import { useId, useMemo } from 'react';

import { ShellLink } from '@/core/components/navigation/shell-link';
import { shellPaths } from '@/core/router/app-paths';
import type { ProductsDict } from '@/routes/app/products/products.types';

import { ProductLocationCombobox } from '../product-location-combobox';

export type ProductFormLocationFieldProps = {
  value: string | null;
  onChange: (v: string | null) => void;
  locations: ProductFormLocation[];
  dict: ProductsDict | undefined;
  knownProductType?: string | null;
  targetKindForReadiness?: ProductBehavior | string;
  required?: boolean;
  requiredMessage?: string;
  locationWarning?: LocationRoutingWarning | null;
  warningMessages?: NonNullable<NonNullable<ProductsDict['edit']>['routing']>;
  onCreateLocation?: () => void;
  createLocationLabel?: string;
  noLocationsTitle?: string;
  noLocationsDescription?: string;
};

export function ProductFormLocationField({
  value,
  onChange,
  locations,
  dict,
  knownProductType,
  targetKindForReadiness,
  required,
  requiredMessage,
  locationWarning,
  warningMessages,
  onCreateLocation,
  createLocationLabel,
  noLocationsTitle,
  noLocationsDescription,
}: ProductFormLocationFieldProps) {
  const labelId = useId();
  const warningText = useMemo(() => {
    if (!locationWarning || !warningMessages) return null;
    if (locationWarning === 'missingGooglePlace')
      return warningMessages.locationMissingGoogle;
    if (locationWarning === 'missingInstagram')
      return warningMessages.locationMissingInstagram;
    if (locationWarning === 'missingFacebook')
      return warningMessages.locationMissingFacebook;
    if (locationWarning === 'missingTiktok')
      return warningMessages.locationMissingTiktok;
    if (locationWarning === 'missingMenu')
      return warningMessages.locationMissingMenu;
    return null;
  }, [locationWarning, warningMessages]);

  const settingsTab =
    locationWarning === 'missingGooglePlace'
      ? 'integrations'
      : 'external-services';
  const settingsHref = value
    ? `${shellPaths.moreWorkspaceLocationDetail(value)}?tab=${settingsTab}`
    : null;

  const showEmptyCreateState =
    locations.length === 0 && !!onCreateLocation && !!createLocationLabel;

  return (
    <div className="space-y-2">
      <Label id={labelId}>
        {dict?.edit?.location}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      {showEmptyCreateState ? (
        <div className="space-y-3 rounded-[var(--radius-nested)] border border-border/70 bg-muted/30 px-4 py-4">
          <div className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
              <MapPin className="size-4 text-muted-foreground" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium text-foreground">
                {noLocationsTitle ?? createLocationLabel}
              </p>
              {noLocationsDescription ? (
                <p className="text-pretty text-xs leading-relaxed text-muted-foreground">
                  {noLocationsDescription}
                </p>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            className="w-full sm:w-auto"
            onClick={onCreateLocation}
          >
            {createLocationLabel}
          </Button>
          {required && requiredMessage ? (
            <p className="text-xs text-destructive">{requiredMessage}</p>
          ) : null}
        </div>
      ) : (
        <ProductLocationCombobox
          labelledBy={labelId}
          locations={locations}
          value={value}
          onChange={onChange}
          knownProductType={knownProductType}
          targetKindForReadiness={targetKindForReadiness}
          placeholder={dict?.edit?.locationPlaceholder ?? 'Select location'}
          searchPlaceholder={
            dict?.edit?.locationSearchPlaceholder ?? 'Search locations…'
          }
          emptyMessage={dict?.edit?.locationSearchEmpty ?? 'No locations found'}
          readyGroupLabel={dict?.edit?.locationReadyGroup ?? 'Ready to use'}
          incompleteGroupLabel={
            dict?.edit?.locationIncompleteGroup ?? 'Needs setup'
          }
          incompleteSuffix={warningMessages?.locationIncompleteSuffix}
          onCreateLocation={onCreateLocation}
          createLocationLabel={createLocationLabel}
        />
      )}
      {!showEmptyCreateState && required && !value && requiredMessage ? (
        <p className="text-xs text-destructive">{requiredMessage}</p>
      ) : null}
      {warningText ? (
        <div className="space-y-1">
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {warningText}
          </p>
          {settingsHref && warningMessages?.openLocationSettings ? (
            <ShellLink
              to={settingsHref}
              className="text-xs font-medium text-primary underline-offset-4 hover:underline"
            >
              {warningMessages.openLocationSettings}
            </ShellLink>
          ) : null}
          {locationWarning && warningMessages?.cannotSaveIncompleteLocation ? (
            <p className="text-xs text-destructive">
              {warningMessages.cannotSaveIncompleteLocation}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
